import { thirdPartyConnectedDataSourceResource } from '@jira/router-resources__third-party-connected-data-source/src/thirdPartyConnectedDataSourceResource.tsx';
import { getIssueCircuitBreaker } from '@jira/router-routes__issue-circuit-breaker/src/index.tsx';
import { userSegRedirectEntrypoint } from '@jira/redirect-entrypoints__user-seg-redirect/src/userSegRedirectEntrypoint.entrypoint.tsx';
import { IssueViewSkeleton } from '@jira/issue__issue-view-skeleton/src/IssueViewSkeleton.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { isCircuitOpen } from '@jira/platform__cookie-circuit-breaker/src/is-circuit-open.tsx';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { loomPermissionResource } from '@jira/router-resources__loom/src/loomPermissionResource.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { confluenceUserPermissionsResource } from '@jira/router-resources__confluence-user-permissions/src/confluenceUserPermissionsResource.tsx';
import { getAIContextPanelResources } from '@jira/router-resources__service-desk-ai-context-panel/src/getAIContextPanelResources.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { issueViewEntryPoint } from '@jira/spa-apps__issue/src/issueViewEntryPoint.entrypoint.tsx';
import { workspaceResource } from '@jira/servicedesk__insight-common-services-workspace-context/src/workspaceResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { isJsmChatOpsEntitlementResourceEnabled } from '@jira/router-resources__jsm-chat-ops/src/isJsmChatOpsEntitlementResourceEnabled.tsx';
import { jsmChatOpsEntitlementResource } from '@jira/router-resources__jsm-chat-ops/src/useJsmChatOpsEntitlementResource.tsx';
import {
	TASK_ABORT,
	TASK_FAIL,
} from '@jira/platform__jira-experience-tracker/src/common/constants.tsx';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
	isWebCrawlerUserAgentWithAppleBot,
} from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { nav4SidebarContentCoreEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/nav4SidebarContentCoreEntryPoint.entrypoint.tsx';
import { nav4ProjectsContentEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-core/src/ui/projects/default/async-content/nav4ProjectsContentEntryPoint.entrypoint.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { ISSUE_VIEW } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { NORMAL_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { fullPageIssuePreloadLayout } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { issueViewRelayResource } from '@jira/router-resources__issue-view/src/useIssueViewRelayRouteResource.tsx';
import { issueGiraDataResource } from '@jira/router-resources__issue-view/src/issueGiraDataResource.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { getOfferingCatalogueFeaturesResource } from '@jira/router-resources__offering-catalogue-features/src/getOfferingCatalogueFeaturesResource.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { queuesNavigationItems as servicedeskNavigationItemsResource } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { jsmProjectMenuContentSidebarResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/jsm-customize-sidebar/index.tsx';
import { browseIssueRoute } from '@jira/router-routes__issue-routes/src/browseIssueRoute.tsx';
import { getDraftReplyPillsResource } from '@jira/router-resources__service-desk-draft-reply/src/getDraftReplyPillsResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectGlobalComponentsResource } from '@jira/router-resources__project-global-components/src/projectGlobalComponentsResource.tsx';
import {
	getJsmIssueViewRelatedArticlesRouteResource,
	getJsmIssueViewSlaPanelResource,
	getJsmIssueViewPanelMetadataResource,
	getJSDSharedNavigationResources,
} from '@jira/router-resources__jsm-issue-view/src/getJsmIssueViewRelatedArticlesRouteResource.tsx';
import { nav4JSWProjectEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-jsw-project-menu/src/ui/jsw-expandable-project/async-content/nav4JSWProjectEntryPoint.entrypoint.tsx';
import { jsmMIMEntitlementResource } from '@jira/router-resources__jsm-major-incident-entitlement/src/useJsmMimEntitlementResource.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { issueCollapsibleGroupDataResource } from '@jira/router-resources__issue-view/src/issueCollapsibleGroupDataResource.tsx';
import { issueAttachmentsSmartlinkDataResource } from '@jira/router-resources__issue-view/src/issueAttachmentsSmartlinkDataResource.tsx';
import { issueDescriptionSmartlinkDataResource } from '@jira/router-resources__issue-view/src/issueDescriptionSmartlinkDataResource.tsx';
import { issueViewRemoteDataResource } from '@jira/router-resources__issue-view/src/issueViewRemoteDataResource.tsx';
import { issueViewResourcesDataResource } from '@jira/router-resources__issue-view/src/issueViewResourcesDataResource.tsx';
import { issueSelectedTabDataResource } from '@jira/router-resources__issue-view/src/issueSelectedTabDataResource.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { gsnSidebarContentEntryPoint } from '@jira/nav-platform-integration__sidebar/src/gsnSidebarContentEntryPoint.entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import sendExperienceAnalytics from '@jira/platform__experience-tracking-analytics/src/index.tsx';
import { truncateStackTrace } from '@jira/issue__issue-view-errors/src/truncateStackTrace.tsx';
import { bifrostBrowseRouteRedirectionHandler } from './common/bifrostBrowseRouteRedirectionHandler.tsx';
import { IssueComponent, LazyIssuePage } from './ui/index.tsx';
import type { Route } from '@atlassian/react-resource-router';
import { serviceDeskContextResource } from '@jira/router-resources__service-desk-context/src/serviceDeskContextResource.tsx';

const getIssueViewReportingMeta = () => {
	return {
		reporting: {
			id: 'routeApp.issue',
			packageName: 'jiraSpa',
			teamName: 'bento',
			// When the gate is on, the platform route error boundary sends the
			// standardized `viewIssue` experience event itself and ignores the custom
			// `onError` below. When off, `experienceName` is undefined and the custom
			// `onError` handler runs as before.
			...(fg('jira_route_error_boundary_experience_event') && {
				experienceName: 'viewIssue',
			}),
			onError: (location: string, error?: Error) => {
				if (fg('bento_suppress_bot_web_crawler')) {
					if (isWebCrawlerUserAgentWithAppleBot()) {
						return;
					}
				}
				const errorStack = fg('bento-1705-add-stack-trace')
					? truncateStackTrace(error?.stack)
					: undefined;
				const userAgent = fg('bento_suppress_bot_web_crawler')
					? (globalThis.navigator?.userAgent ?? 'unknown')
					: undefined;
				const isWebDriver = fg('bento_suppress_bot_web_crawler')
					? globalThis.navigator?.webdriver
					: undefined;
				if (fg('issue_view_ignore_client_error_in_analytics')) {
					// Network errors (e.g. connection drops during SPA transitions) are not attributable to the product
					// We want to ignore these errors and not count them towards the SLO
					if (error && isClientFetchError(error)) {
						return;
					}
					const isAbortable = error && isHttpClientErrorResponse(error);
					sendExperienceAnalytics({
						experience: 'viewIssue',
						analyticsSource: 'issueViewRoute',
						action: isAbortable ? TASK_ABORT : TASK_FAIL,
						wasExperienceSuccesful: false,
						application: null,
						edition: null,
						additionalAttributes: {
							errorMessage: error?.message ?? 'Unknown error',
							location,
							...(fg('bento-1705-add-stack-trace') && { errorStack }),
							...(userAgent !== undefined && { userAgent }),
							...(isWebDriver !== undefined && { isWebDriver }),
						},
					});
				} else {
					sendExperienceAnalytics({
						experience: 'viewIssue',
						analyticsSource: 'issueViewRoute',
						action: TASK_FAIL,
						wasExperienceSuccesful: false,
						application: null,
						edition: null,
						additionalAttributes: {
							errorMessage: error?.message ?? 'Unknown error',
							location,
							...(fg('bento-1705-add-stack-trace') && { errorStack }),
							...(userAgent !== undefined && { userAgent }),
							...(isWebDriver !== undefined && { isWebDriver }),
						},
					});
				}
			},
		},
	};
};

// These resources have been re-ordered. We have a theory that moving the more expensive ones to be first in the list makes them faster in SSR, there some small overhead that compounds over the 30 odd requests that happens.
// Longest poles at the moment are issueViewRelayResource and issueGiraDataResource, so we have moved them to the top of the list.
const resources = () => [
	issueViewRelayResource,
	resourceWithCondition(
		() => fg('dfo_issue_view_remote_data_srr_group'),
		issueViewRemoteDataResource,
	),
	resourceWithCondition(() => !fg('remove-issueviewinteractivequery-m4'), issueGiraDataResource),
	...getIssueViewResources(),
	...getNavigationResources({ isUsingSidebarEntrypoint: fg('move-issue-view-to-entrypoint') }),
	projectContextResource,
	resourceWithCondition(() => fg('jira-issue-view-contour-theme-enabled'), projectThemeResource),
	staticSidebarShortcutsResource,
	staticProjectSidebarNavigationResource,
	...getOfferingCatalogueFeaturesResource(),
	servicedeskNavigationItemsResource,
	workspaceResource,
	getConsolidationStateResource(),
	...getAIContextPanelResources(),
	...getResolutionPlanSSRResource(),
	...getDraftReplyPillsResource(),
	projectGlobalComponentsResource,
	getJsmIssueViewSlaPanelResource(),
	resourceWithCondition(
		() => fg('jsm_flash_ssr_major_incident_management_fields'),
		jsmMIMEntitlementResource,
	),
	getJsmIssueViewRelatedArticlesRouteResource(),
	getJsmIssueViewPanelMetadataResource(),
	issueSelectedTabDataResource,
	resourceWithCondition(
		() => fg('jfp-magma-ssr-iv-collapse-group'),
		issueCollapsibleGroupDataResource,
	),
	resourceWithCondition(
		() => fg('jpx-833-smart-links-graphql-provider'),
		issueDescriptionSmartlinkDataResource,
	),
	resourceWithCondition(
		() =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('attachments_consolidation_smart_links_ssr') &&
			expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
		issueAttachmentsSmartlinkDataResource,
	),
	jsmProjectMenuContentSidebarResource,
	resourceWithCondition(
		() => !fg('dfo_issue_view_remote_data_srr_group'),
		issueViewRemoteDataResource,
	),
	resourceWithCondition(
		() => expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
		issueViewResourcesDataResource,
	),
	resourceWithCondition(
		() => fg('jira-issue-view-3p-attachments'), // additional FG that need 3P resources should use or condition
		thirdPartyConnectedDataSourceResource,
	),
	currentUserResource,
	...getJSDSharedNavigationResources(),
	loomPermissionResource,
	resourceWithCondition(
		() => fg('fg-perf-issue-view-confluence-menu-optimisation'),
		confluenceUserPermissionsResource,
	),
	resourceWithCondition(isJsmChatOpsEntitlementResourceEnabled, jsmChatOpsEntitlementResource),
	resourceWithCondition(() => fg('ssr_servicedesk_context_'), serviceDeskContextResource),
];

const entrypoint = createPageEntryPoint({
	main: issueViewEntryPoint,
	leftSidebar: {
		sidebar: nav4SidebarContentCoreEntryPoint,
		entryPoints: {
			projects: { entryPoint: nav4ProjectsContentEntryPoint },
			boards: { entryPoint: nav4JSWProjectEntryPoint },
		},
		isProjectsExpanded: true,
	},
	redirect: userSegRedirectEntrypoint,
});

const GSNEntrypoint = createPageEntryPoint({
	main: issueViewEntryPoint,
	leftSidebar: {
		sidebar: gsnSidebarContentEntryPoint,
		entryPoints: {
			projects: { entryPoint: nav4ProjectsContentEntryPoint },
			boards: { entryPoint: nav4JSWProjectEntryPoint },
		},
		isProjectsExpanded: true,
	},
	redirect: userSegRedirectEntrypoint,
});
/*
 * We inline the early script in the payload even though most of the time it is not used.
 * this function will ensure we only inline the script when it actually does something.
 */
function getEarlyEntry(): string[] {
	// ensure code is removed from client-side bundle
	if (__SERVER__) {
		const circuitBreakerConfig = getIssueCircuitBreaker();
		const shouldFetchWhenSSREnabled =
			isCircuitOpen(circuitBreakerConfig) && circuitBreakerConfig?.isCircuitBreakerEnabled;
		if (!shouldFetchWhenSSREnabled) {
			return ['parse-gates-early-entry'];
		}

		return ['issue-app-early-entry'];
	}
	return ['issue-app-early-entry'];
}

export const browseIssueRouteEntry: Route = createEntry(browseIssueRoute, () => ({
	isStrictModeEnabled: fg('kenji-jira-incremental-strict-mode'),
	group: 'issue',
	isHydrateRootEnabled: (projectType?: ProjectType) => {
		switch (projectType) {
			case 'service_desk':
			case 'customer_service':
				return fg('jfp-magma-issue-view-full-page-hydrate-root-jsm');
			default:
				return true;
		}
	},
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-iv'),
	preloadOptions: {
		earlyChunks: getEarlyEntry(),
	},
	analyticsOptions: {
		// CMON-3192 [POC]: emit a SignalFx counter at SSR time for the viewIssue
		// experience. This lets CMON detect "blind spot" failures (DB down, gateway
		// crash, SPA bootstrap failure) where no taskSuccess/taskFail is fired client-side.
		// Gate: jfp-cmon-task-start-issue-view controls emission at call site (multi-spa-route.tsx).
		taskStart: { experience: 'viewIssue' },
	},
	earlyAssets: NORMAL_EARLY_ASSET_STRATEGY,
	// eslint-disable-next-line no-restricted-syntax
	component: fg('move-issue-view-to-entrypoint') ? undefined : IssueComponent,
	skeleton: IssueViewSkeleton,
	layout: composeLayouts(genericProjectLayout, fullPageIssuePreloadLayout),
	navigation: {
		menuId: MenuIdProjects,
		sidebarId: fg('move-issue-view-to-entrypoint') ? SidebarIdContent : undefined,
	},
	resources: resources(),
	forPaint: [LazyAtlassianNavigation, LazyIssuePage],
	ufoName: 'issue-view',
	fallbackExperience: 'viewIssue',
	meta: {
		capability: ISSUE_VIEW,
		...getIssueViewReportingMeta(),
	},
	bifrost: {
		bifrostGroup: 'issue-view',
		manifestName: 'jira-spa-issue-view.json',
		circuitBreakerConfig: getIssueCircuitBreaker(),
		redirects: [
			{
				key: 'issue-project-context-redirect',
				handler: bifrostBrowseRouteRedirectionHandler,
				timeoutMs: 1000,
			},
		],
	},
	supportsPreviewPanel: true,
	supportsProjectTheme: 'contoured',
	entryPoint: fg('move-issue-view-to-entrypoint')
		? () => {
				return fg('navx-global-side-nav') ? GSNEntrypoint : entrypoint;
			}
		: undefined,
	entryPointOptions: fg('jfp-magma-concurrent-entrypoint-router-metric-fix')
		? { isSSRPlaceholderEnabled: true }
		: undefined,
	excludeFromWrmDeferredLoading: true,
}));
